// The office's notifications place (QA-097; the office-notifications prototype): a bell in the
// header with the unread count (red when one is urgent), and a sheet over the screen listing what
// the server gives this person (notifications.list, by permission), urgent first. Each item opens
// its source and is marked read; one or all can be marked read here. The list follows the live
// stream, so a new one counts in the header without a reload.

import { useCallback, useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { useOnline } from '../connection/connection.ts';
import { addRows } from '../blueprint/paged.ts';
import { Loading } from './Loading.tsx';
import { shownText } from './pages.ts';
import { groupNotes, notesInput, noteTime, type Note, type NotesPage } from './notifications-model.ts';
import './notifications.css';

export interface NotesState {
  /** Null until the first answer. */
  notes: Note[] | null;
  unread: number;
  urgent: number;
  /** Every notification the person has, the server's count (the list reads a page at a time). */
  total: number;
  /** The next page, when there is one and none is being read (list caps, BL-110). */
  more: (() => void) | null;
  /** A next page is being read. */
  busy: boolean;
  error: string | null;
  reload: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
}

/**
 * The signed-in person's notifications, read once and again whenever `reload` is called: a page at a
 * time, a refresh reading again as many as were shown. The counts are the server's, of all of them.
 */
export function useNotifications(on: boolean): NotesState {
  const [data, setData] = useState<{
    notes: Note[];
    unread: number;
    urgent: number;
    total: number;
    next: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = useRef(0);
  shown.current = data?.notes.length ?? 0;
  // a page that comes back after a refresh began is dropped: the refresh reads as many again
  const reads = useRef(0);
  const reload = useCallback(async () => {
    if (!on) return;
    const read = ++reads.current;
    try {
      const r = await client.call<NotesPage>('notifications.list', notesInput(shown.current));
      if (read !== reads.current) return;
      setData({ notes: r.notifications, unread: r.unread, urgent: r.urgent, total: r.total, next: r.next });
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Couldn’t load notifications.'));
    }
  }, [on]);
  const more = useCallback(() => {
    const after = data?.next;
    if (!after) return;
    const read = reads.current;
    setBusy(true);
    client
      .call<NotesPage>('notifications.list', notesInput(0, after))
      .then((r) => {
        if (read !== reads.current) return;
        setData(
          (d) =>
            d && {
              notes: addRows(d.notes, r.notifications),
              unread: r.unread,
              urgent: r.urgent,
              total: r.total,
              next: r.next,
            },
        );
      })
      .catch((e: unknown) => setError(errorText(e, 'Couldn’t load the rest.')))
      .finally(() => setBusy(false));
  }, [data?.next]);
  useEffect(() => {
    if (on) void reload();
    else setData(null);
  }, [on, reload]);
  const markRead = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return;
      await client.call('notifications.markRead', { ids });
      await reload();
    },
    [reload],
  );
  return {
    notes: data?.notes ?? null,
    unread: data?.unread ?? 0,
    urgent: data?.urgent ?? 0,
    total: data?.total ?? 0,
    more: data?.next && !busy ? more : null,
    busy,
    error,
    reload,
    markRead,
  };
}

const BELL = (
  <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 10a6 6 0 0 1 12 0c0 4.5 1.6 6.2 2.4 7H3.6C4.4 16.2 6 14.5 6 10ZM10 20.2a2.2 2.2 0 0 0 4 0" />
  </svg>
);

export function NotificationsBell({
  notes,
  timeZone,
  onOpen,
}: {
  notes: NotesState;
  timeZone?: string;
  /** Opens a notification's source (the caller decides where, and whether this person may). */
  onOpen: (n: Note) => void;
}) {
  const [open, setOpen] = useState(false);
  const { unread, urgent } = notes;
  const label = `Notifications: ${unread ? `${unread} unread${urgent ? `, ${urgent} urgent` : ''}` : 'nothing new'}`;
  return (
    <>
      <button
        className={`btn soft hbtn bell${open ? ' on' : ''}`}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {BELL}
        <span className="lbl">Notifications</span>
        {unread > 0 && (
          <span className={`count${urgent ? ' urgent' : ''}`} aria-hidden="true">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <NotificationsSheet
          notes={notes}
          {...(timeZone ? { timeZone } : {})}
          onClose={() => setOpen(false)}
          onOpen={(n) => {
            setOpen(false);
            onOpen(n);
          }}
        />
      )}
    </>
  );
}

function NotificationsSheet({
  notes,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  onClose,
  onOpen,
}: {
  notes: NotesState;
  timeZone?: string;
  onClose: () => void;
  onOpen: (n: Note) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const online = useOnline();
  // L-action: the pressed button says what it's doing, keeps its width, and can't be pressed twice.
  const [busy, setBusy] = useState<{ key: string; width: number } | null>(null);
  const [failed, setFailed] = useState<{ key: string; message: string } | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    void notes.reload(); // the last list shows at once; this brings it up to date (once, on opening)
  }, []);
  const mark = async (key: string, ids: string[], el: HTMLElement) => {
    if (busy) return;
    setBusy({ key, width: el.offsetWidth });
    setFailed(null);
    try {
      await notes.markRead(ids);
    } catch (e) {
      setFailed({ key, message: errorText(e, 'That didn’t go through. Try again.') });
    } finally {
      setBusy(null);
    }
  };
  const openNote = (n: Note) => {
    if (!n.read) void notes.markRead([n.id]).catch(() => undefined);
    onOpen(n);
  };
  const now = new Date();
  const list = notes.notes;
  const unreadIds = (list ?? []).filter((n) => !n.read).map((n) => n.id);
  const hold = (key: string) =>
    busy?.key === key ? { 'aria-disabled': true as const, style: { minWidth: busy.width } } : {};
  return (
    <dialog
      ref={ref}
      className="sheet notes-sheet"
      aria-labelledby="notes-title"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) ref.current.close(); // a tap on the backdrop
      }}
    >
      <div className="notes-head">
        <h2 id="notes-title">Notifications</h2>
        <form method="dialog">
          <button className="btn soft" type="submit">
            Close
          </button>
        </form>
      </div>
      <div className="notes-status" aria-live="polite">
        {!online && <p className="say off">No connection: new notifications arrive when it’s back.</p>}
        {list && notes.error && (
          <p className="say bad" role="alert">
            Couldn’t load notifications.{' '}
            <button className="btn soft" onClick={() => void notes.reload()}>
              Try again
            </button>
          </p>
        )}
      </div>
      <div className="notes-body">
        {!list ? (
          <Loading error={notes.error} what="your notifications" retry={() => void notes.reload()} />
        ) : list.length === 0 ? (
          <p className="empty-line">Nothing needs you right now.</p>
        ) : (
          groupNotes(list, now, timeZone).map((g) => (
            <section key={g.key} className="ngroup" aria-labelledby={`notes-${g.key}`}>
              <h3 id={`notes-${g.key}`}>{g.label}</h3>
              <ul>
                {g.notes.map((n) => (
                  <li key={n.id} className={`nitem${n.read ? ' read' : n.urgent ? ' urgent' : ''}`}>
                    <span className="ntime">{noteTime(n.createdAt, now, timeZone)}</span>
                    <button className="ntitle" onClick={() => openNote(n)}>
                      {n.title}
                    </button>
                    {n.body && <p className="nwhy">{n.body}</p>}
                    {!n.read && (
                      <div className="nact">
                        <button
                          className="btn"
                          aria-label={`Mark read: ${n.title}`}
                          disabled={!online}
                          {...hold(n.id)}
                          onClick={(e) => void mark(n.id, [n.id], e.currentTarget)}
                        >
                          {busy?.key === n.id ? 'Marking…' : 'Mark read'}
                        </button>
                        {failed?.key === n.id && (
                          <span className="nerr" role="alert">
                            {failed.message}
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
        {list && (notes.more || notes.busy) && (
          <p className="notes-more">
            <span className="muted small">{shownText(list.length, notes.total)}</span>
            <button className="btn soft" disabled={notes.busy} onClick={() => notes.more?.()}>
              {notes.busy ? 'Loading…' : 'Load more'}
            </button>
          </p>
        )}
      </div>
      {unreadIds.length > 0 && (
        <div className="notes-foot">
          <button
            className="btn"
            disabled={!online}
            {...hold('all')}
            onClick={(e) => void mark('all', unreadIds, e.currentTarget)}
          >
            {busy?.key === 'all' ? 'Marking…' : 'Mark all read'}
          </button>
          {!online ? (
            <span className="nnote">Marking read needs a connection</span>
          ) : (
            failed?.key === 'all' && (
              <span className="nerr" role="alert">
                {failed.message}
              </span>
            )
          )}
        </div>
      )}
    </dialog>
  );
}
